import { PHONE_TYPE_TO_HUMAN_READABLE, PROSPECT_PHONE_TYPE, Platform } from 'interfaces/db';
import { SETTING, MusicWebsite, DialingMode, OutreachModalOnClickSetting, PhoneKeys, SettingValueType, PhonePreference, NumberRotationStrategy } from 'interfaces/settings';

export const DEFAULT_POWER_RANKINGS = [PROSPECT_PHONE_TYPE.MOBILE, PROSPECT_PHONE_TYPE.VOIP, PROSPECT_PHONE_TYPE.WORK, PROSPECT_PHONE_TYPE.HOME, PROSPECT_PHONE_TYPE.OTHER];

// Helper function to get phone types for a specific dialer
export function getPhoneTypesForDialer(dialer: Platform): PROSPECT_PHONE_TYPE[] {
  return DEFAULT_POWER_RANKINGS.filter((phone_type) => {
    if (phone_type === PROSPECT_PHONE_TYPE.MOBILE) return true;
    if (phone_type === PROSPECT_PHONE_TYPE.VOIP) return [Platform.OUTREACH, Platform.APOLLO].includes(dialer);
    if (phone_type === PROSPECT_PHONE_TYPE.WORK) return true;
    if (phone_type === PROSPECT_PHONE_TYPE.HOME) return [
      Platform.APOLLO,
      Platform.OUTREACH, Platform.VANILLASOFT,
      Platform.SALESLOFT, Platform.GOHIGHLEVEL,
      Platform.CLAY, Platform.GOOGLE_SHEETS,
      Platform.GONG_ENGAGE].includes(dialer)
    if (phone_type === PROSPECT_PHONE_TYPE.OTHER) return dialer !== Platform.SALESLOFT;
    return false;
  });
}

export const getPhoneLabel = (phoneType: PROSPECT_PHONE_TYPE, selectedPlatform: Platform | null) => {
  switch (phoneType) {
      case PROSPECT_PHONE_TYPE.MOBILE: return 'Mobile'
      case PROSPECT_PHONE_TYPE.WORK:
        if (selectedPlatform === Platform.APOLLO) return 'Work - Direct'
        return 'Work'
      case PROSPECT_PHONE_TYPE.VOIP:
        if (selectedPlatform === Platform.APOLLO) return 'Corporate - Phone'
        return 'VoIP';
      case PROSPECT_PHONE_TYPE.HOME:
        if (selectedPlatform === Platform.VANILLASOFT) return 'Direct';
        return 'Home';
      case PROSPECT_PHONE_TYPE.OTHER: return 'Other';
      default: return PHONE_TYPE_TO_HUMAN_READABLE[phoneType]
    }
}

export const SETTING_TO_DEFAULT_VALUE: {[k in SETTING]: (dialer: Platform) => SettingValueType<SETTING>} = {
  // ** synced settings **//
  [SETTING.TIME_TO_DIAL]: (dialer) => 4,
  [SETTING.TIME_TO_LOG]: (dialer) => 4,
  [SETTING.AUTO_PROGRESS_LIVE_ACTIVE]: (dialer) => true,
  [SETTING.AUTO_PROGRESS_TIME]: (dialer) => 60,
  [SETTING.HANGUP_RINGING_ACTIVE]: (dialer) => true,
  [SETTING.HANGUP_RINGING_TIME]: (dialer) => 35,
  [SETTING.HANGUP_VOICEMAIL_ACTIVE]: (dialer) => true,
  [SETTING.HANGUP_VOICEMAIL_TIME]: (dialer) => 0,
  [SETTING.MUSIC_WEBSITE]: (dialer) => MusicWebsite.SPOTIFY,
  [SETTING.LOCAL_CALL_TIME_MIN]: (dialer) => 8, // 8 AM
  [SETTING.LOCAL_CALL_TIME_MAX]: (dialer) => 18, // 6 PM
  [SETTING.DIALING_MODE]: (dialer) => DialingMode.MULTI_NUMBER_ATTEMPT,
  [SETTING.PHONE_PREFERENCE]: (dialer) => {
    return Object.fromEntries(getPhoneTypesForDialer(dialer).map((v, idx) => [v, { [PhoneKeys.ORDER]: idx, [PhoneKeys.ACTIVE]: true, [PhoneKeys.DOUBLE_TAPS]: 1 }])) as Record<PROSPECT_PHONE_TYPE, PhonePreference>;
  },
  [SETTING.NO_AUTO_COMPLETE_TASK]: (dialer) => false,
  [SETTING.ACTION_ON_BAD_NUMBER]: (dialer) => true,
  [SETTING.NO_SUMMARY_AUTOMATION]: (dialer) => false,
  [SETTING.HIDE_WRONG_NUMBER_ELEMENT]: (dialer) => false,
  [SETTING.NO_LOG_ONLY]: (dialer) => false,
  // *** for apollo *** //
  [SETTING.PRIMARY_ALWAYS]: (dialer) => false,
  [SETTING.PRIMARY_FIRST]: (dialer) => false,
  [SETTING.VERIFIED_ALWAYS]: (dialer) => false,
  [SETTING.VERIFIED_FIRST]: (dialer) => false,
  // *** for outreach *** ///
  [SETTING.OUTREACH_MODAL_CLICK_SETTING]: (dialer) => dialer === Platform.OUTREACH ? OutreachModalOnClickSetting.RECORD_ONE_PARTY : null,
  // *** for hubspot *** ///
  [SETTING.ALL_RECORDS]: (dialer) => true,
  // *** generic *** //
  [SETTING.LINKEDIN_AUTO_OPEN]: (dialer) => false,
  [SETTING.COMPANY_WEBSITE_AUTO_OPEN]: (dialer) => false,
  [SETTING.SALESFORCE_URL_AUTO_OPEN]: (dialer) => false,
  [SETTING.CUSTOM_URL_AUTO_OPEN]: (dialer) => false,
  [SETTING.DNC_ALWAYS]: (dialer) => false,
  [SETTING.MAX_NUMBER_LINES]: (dialer) => 5,
  [SETTING.POWER_CALL_SHOULD_RING]: (dialer) => true,
  [SETTING.INCOMING_CALL_MUTED]: (dialer) => false,
  [SETTING.TEAM_CALL_MUTED]: (dialer) => false,
  [SETTING.AUTOMATIC_EXTENSION_NUMBER]: (dialer) => false,
  [SETTING.ADD_TRANSCRIPT_TO_SUMMARY_SETTING]: (dialer) => false,
  [SETTING.PLAY_BEEP_ON_CONNECT]: (dialer) => false,
  [SETTING.UNIVERSAL_CONTROLLER_ENABLED]: (dialer) => false,
  [SETTING.NO_NAVIGATION_ON_CONNECT]: (dialer) => false,
  [SETTING.NO_CANCEL_CALLS]: (dialer) => false,
  [SETTING.DEFAULT_NUMBER_ROTATION_STRATEGY]: (dialer) => NumberRotationStrategy.GEOGRAPHICALLY_CLOSEST,
  [SETTING.USE_NUMBER_FOR_PRIORITY_CALCULATING_TIMEZONE]: (dialer) => false,
  [SETTING.HIDE_CLICK_AUTODIALER]: (dialer) => false,
  [SETTING.HANGUP_SCREEN]: (dialer) => false,
  [SETTING.VM_NAME_MATCH_DETECTION]: (dialer) => false,
}